import { api } from './client';

export interface CollabParticipant {
  id: string;
  displayName: string;
  avatarUrl?: string;
  activity: string;
  isActive: boolean;
  role: string;
}

export interface CollabAnnotationPosition {
  start?: number;
  end?: number;
  selectedText?: string;
  tabId?: string;
  tabTitle?: string;
  tabUrl?: string;
  x?: number;
  y?: number;
}

export interface CollabAnnotation {
  id: string;
  type: string;
  userId: string;
  displayName: string;
  content: string;
  color: string;
  position?: CollabAnnotationPosition;
  isResolved: boolean;
  createdAt?: string;
}

export interface CollabComment {
  id: string;
  userId: string;
  displayName: string;
  avatarUrl?: string;
  content: string;
  timestamp: string;
}

export interface CollabSession {
  id: string;
  name: string;
  ownerId: string;
  status: string;
  maxParticipants: number;
  inviteCode: string;
  participants: CollabParticipant[];
  annotations?: CollabAnnotation[];
  comments?: CollabComment[];
  createdAt?: string;
}

export async function listCollabSessions(): Promise<CollabSession[]> {
  const { data } = await api.get<CollabSession[]>('/collaboration/sessions');
  return Array.isArray(data) ? data : [];
}

export async function getCollabSession(id: string): Promise<CollabSession> {
  const { data } = await api.get<CollabSession>(`/collaboration/sessions/${id}`);
  return data;
}

export async function createCollabSession(payload: {
  name: string;
  maxParticipants?: number;
}): Promise<CollabSession> {
  const { data } = await api.post<CollabSession>('/collaboration/sessions', payload);
  return data;
}

export async function updateCollabSession(
  id: string,
  payload: Partial<{ name: string; maxParticipants: number; status: string }>,
): Promise<CollabSession> {
  const { data } = await api.put<CollabSession>(`/collaboration/sessions/${id}`, payload);
  return data;
}

export async function deleteCollabSession(id: string): Promise<void> {
  await api.delete(`/collaboration/sessions/${id}`);
}

export async function joinCollabSession(
  inviteCode: string,
  displayName?: string,
): Promise<CollabSession> {
  const { data } = await api.post<CollabSession>('/collaboration/sessions/join', {
    inviteCode,
    displayName,
  });
  return data;
}

export async function addCollabAnnotation(
  sessionId: string,
  annotation: Partial<CollabAnnotation>,
): Promise<CollabAnnotation> {
  const { data } = await api.post<CollabAnnotation>(
    `/collaboration/sessions/${sessionId}/annotations`,
    annotation,
  );
  return data;
}

export async function resolveCollabAnnotation(
  sessionId: string,
  annotationId: string,
): Promise<void> {
  await api.patch(`/collaboration/sessions/${sessionId}/annotations/${annotationId}`);
}

export async function addCollabComment(
  sessionId: string,
  comment: { content: string; userId?: string; displayName?: string },
): Promise<CollabComment> {
  const { data } = await api.post<CollabComment>(
    `/collaboration/sessions/${sessionId}/comments`,
    comment,
  );
  return data;
}
